<script setup>
import { onMounted, onUnmounted, ref, watchEffect } from "vue";
import {
  checkPayment,
  exportReservationDetail,
  getReservationDetailById,
  getResource,
} from "@/api/httpApi";
import { useStore } from "vuex";

const store = useStore();

const resource = ref();
const resourceId = ref();
const reservationIdDetails = ref();
const isMobile = ref(false);

// VARIABLES FOR COMUNE
const faqProperties = ref({
  label:
    window.drupalSettings?.stepForm?.faqMunicipality?.label ||
    "Leggi le domande frequenti",
  link: window.drupalSettings?.stepForm?.faqMunicipality?.url,
});
const helpProperties = ref({
  label:
    window.drupalSettings?.stepForm?.helpMunicipality?.label ||
    "Richiedi assistenza",
  link: window.drupalSettings?.stepForm?.helpMunicipality?.url,
});
const callProperties = ref({
  label:
    window.drupalSettings?.stepForm?.callMunicipality?.label ||
    "Chiama il numero verde 05 0505",
  link: window.drupalSettings?.stepForm?.callMunicipality?.url,
});
const appointmentProperties = ref({
  label:
    window.drupalSettings?.stepForm?.appointmentMunicipality?.label ||
    "Prenota appuntamento",
  link: window.drupalSettings?.stepForm?.appointmentMunicipality?.url,
});
const disserviceProperties = ref({
  label:
    window.drupalSettings?.stepForm?.disserviceMunicipality?.label ||
    "Segnala disservizio",
  link: window.drupalSettings?.stepForm?.disserviceMunicipality?.url,
});

const handleResizePaymentStep = () => {
  isMobile.value = window.innerWidth < 768;
};

const onPrint = async () => {
  try {
    await exportReservationDetail(reservationIdDetails.value.reservationId);
  } catch (error) {
    console.error(error);
  }
};

watchEffect(async () => {
  if (
    resourceId.value &&
    (!resource.value || resource.value.resourceId !== resourceId.value)
  ) {
    try {
      resource.value = await getResource(resourceId.value);
    } catch (err) {
      console.error(err);
      resource.value = undefined;
    }
  }
});

watchEffect(async () => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchReservationId = searchParams.get("paymentReservationId");
  const searchTokenPagoPa =
    searchParams.get("token") || sessionStorage.getItem("TOKEN_PAGO_PA");
  if (searchReservationId) {
    try {
      const payloadCheckPayment = {
        token: searchTokenPagoPa,
      };

      await checkPayment(payloadCheckPayment);

      reservationIdDetails.value = await getReservationDetailById(
        searchReservationId
      );
      store.commit(
        "setReservationId",
        reservationIdDetails?.value?.reservationId
      );
      resourceId.value = reservationIdDetails.value?.resourceId;
    } catch (err) {
      console.error(err);
    }
  }
});

const prepareLabelPayment = (status) => {
  switch (status) {
    case "OK":
      return resource?.value?.paymentSuccessText || "Pagamento effettuato";
    case "ERROR":
      return resource?.value?.paymentFailureText || "Pagamento rifiutato";
    case "DEFERRED":
      return resource?.value?.paymentDeferredText || "Pagamento differito";
    default:
      return status;
  }
};

onMounted(() => {
  handleResizePaymentStep();
  window.addEventListener("resize", handleResizePaymentStep);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResizePaymentStep);
});
</script>

<template>
  <div class="container-fluid row mx-auto p-2">
    <div class="col-12 col-md-6 mb-5 px-0 px-md-3">
      <p>
        Codice prenotazione:
        <strong> {{ reservationIdDetails?.reservationId || "-" }}</strong>
      </p>
      <p>
        {{ prepareLabelPayment(reservationIdDetails?.paymentLastOutcome) }}
      </p>
      <p
        :class="`${
          reservationIdDetails?.paymentLastOutcome === 'OK' ? 'mb-0' : ''
        }`"
      >
        Importo:
        <strong>{{
          reservationIdDetails?.paymentAmount
            ? `${reservationIdDetails?.paymentAmount}€`
            : "-"
        }}</strong>
      </p>
      <p v-if="reservationIdDetails?.paymentLastOutcome === 'OK'">
        Inviato il:
        <strong>{{ reservationIdDetails?.paymentLastOutcome || "-" }}</strong>
      </p>
      <br />
      <p>
        Abbiamo inviato il riepilogo all’email:
        <strong>{{ reservationIdDetails?.email || "-" }}</strong>
      </p>
      <button
        type="button"
        class="mt-4 btn btn-outline-primary btn-lg d-flex align-items-center justify-content-center gap-2 col-12 col-lg-6"
        @click="onPrint"
      >
        <svg
          class="icon icon-md align-self-start icon-primary icon-color"
          aria-hidden="true"
        >
          <use
            :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-download`"
          ></use>
        </svg>
        <span>Scarica la ricevuta (PDF 100KB)</span>
      </button>
    </div>
    <div
      v-if="!isMobile"
      class="col-12 col-md-6 mb-5 d-flex flex-column align-items-end"
    ></div>

    <div class="bg-light shadow-contacts p-0 p-md-5">
      <div class="row d-flex justify-content-center">
        <div class="col-12 col-lg-6 p-contacts">
          <div class="card w-100">
            <div
              class="card-body p-0 pt-5 ps-md-4 pt-md-4 pe-md-4 link-list-wrapper"
            >
              <h4 class="title-medium-2-semi-bold">Contatta il comune</h4>
              <ul class="link-list mt-3">
                <li class="">
                  <a
                    class="list-item icon-left t-primary title-small-semi-bold"
                    :href="faqProperties.link"
                  >
                    <span class="list-item-title-icon-wrapper">
                      <svg
                        class="icon icon-sm align-self-start icon-primary icon-color"
                        aria-hidden="true"
                      >
                        <use
                          :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-help-circle`"
                        ></use>
                      </svg>
                      <span
                        class="list-item-title title-small-semi-bold fw-normal"
                        >{{ faqProperties.label }}</span
                      >
                    </span>
                  </a>
                </li>
                <li class="">
                  <a
                    class="list-item icon-left t-primary title-small-semi-bold"
                    :href="helpProperties.link"
                  >
                    <span class="list-item-title-icon-wrapper">
                      <svg
                        class="icon icon-sm align-self-start icon-primary icon-color"
                        aria-hidden="true"
                      >
                        <use
                          :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-mail`"
                        ></use>
                      </svg>
                      <span
                        class="list-item-title title-small-semi-bold fw-normal"
                        >{{ helpProperties.label }}</span
                      >
                    </span>
                  </a>
                </li>
                <li class="">
                  <a
                    class="list-item icon-left t-primary title-small-semi-bold"
                    :href="callProperties.link"
                  >
                    <span class="list-item-title-icon-wrapper">
                      <svg
                        class="icon icon-sm align-self-start icon-primary icon-color"
                        aria-hidden="true"
                      >
                        <use
                          :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-hearing`"
                        ></use>
                      </svg>
                      <span
                        class="list-item-title title-small-semi-bold fw-normal"
                        >{{ callProperties.label }}</span
                      >
                    </span>
                  </a>
                </li>
                <li class="">
                  <a
                    class="list-item icon-left t-primary title-small-semi-bold"
                    :href="appointmentProperties.link"
                  >
                    <span class="list-item-title-icon-wrapper">
                      <svg
                        class="icon icon-sm align-self-start icon-primary icon-color"
                        aria-hidden="true"
                      >
                        <use
                          :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-calendar`"
                        ></use>
                      </svg>
                      <span
                        class="list-item-title title-small-semi-bold fw-normal"
                        >{{ appointmentProperties.label }}</span
                      >
                    </span>
                  </a>
                </li>
              </ul>
              <h4 class="title-medium-2-semi-bold">Problemi in città</h4>
              <ul class="link-list mt-3 mb-0">
                <li class="">
                  <a
                    class="list-item icon-left t-primary title-small-semi-bold"
                    :href="disserviceProperties.link"
                  >
                    <span class="list-item-title-icon-wrapper">
                      <svg
                        class="icon icon-sm align-self-start icon-primary icon-color"
                        aria-hidden="true"
                      >
                        <use
                          :href="`/${store.state.modulePath}/assets/svg/sprites.svg#it-map-marker-circle`"
                        ></use>
                      </svg>
                      <span
                        class="list-item-title title-small-semi-bold fw-normal"
                        >{{ disserviceProperties.label }}</span
                      >
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
